import { useEffect, useState } from 'react'
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import {
  AlertCircle, Boxes, Building2, CalendarCheck, CalendarClock, CheckCheck, ClipboardList, CloudOff, CreditCard, FileText, Gauge, HardHat, IndianRupee, LogOut, Menu, Package, PackageCheck, PackageMinus, PackagePlus, ReceiptIndianRupee, ReceiptText, RefreshCw, Repeat, ScrollText, Settings, ShieldCheck, SlidersHorizontal, Brush, Truck, Users, Wallet, X, PackageSearch, Landmark, Banknote,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { BUILD_LABEL } from '@/lib/build'
import { can, isReadOnly, ROLE_LABEL } from '@/lib/domain'
// Who sees which screen is POLICY, and lives in one plain module so the
// tests can read it without importing this component.
import { canSeeRoute } from '@/lib/nav'
import { landingFor, mayUseModule } from '@/lib/modules'
import { flushOutbox, useSyncStore } from '@/lib/sync'
import type { UserRole } from '@/lib/types'
import { Spinner } from '@/components/ui'
import { NotificationBell } from './NotificationBell'
import { ChangePasswordButton } from './ChangePassword'
// The seam between the two sides of the application. Draws nothing for the
// ~45 people entitled to Materials only, which is nearly everybody.
import { ModuleSwitch } from './ModuleSwitch'
import { MobileTabBar, MobileTabSpacer, pickMobileTabs, type MobileTab } from './MobileTabBar'

interface NavEntry {
  to: string
  label: string
  icon: typeof Gauge
  group: string
}

const NAV: NavEntry[] = [
  { to: '/', label: 'Dashboard', icon: Gauge, group: 'Overview' },

  { to: '/indents', label: 'Site Indents', icon: HardHat, group: 'Requisition' },
  { to: '/prs', label: 'Purchase Requisitions', icon: ClipboardList, group: 'Requisition' },
  { to: '/ists', label: 'Inter-Site Transfers', icon: Repeat, group: 'Requisition' },

  { to: '/pos', label: 'Purchase Orders', icon: FileText, group: 'Procurement' },
  // 30-09-2026, migration 0188: cancel or close requisitions no longer needed.
  { to: '/pr-housekeeping', label: 'Requisition Housekeeping', icon: Brush, group: 'Procurement' },
  { to: '/payables', label: 'Vendor Payables', icon: Wallet, group: 'Procurement' },
  // Asked for by Accounts: every vendor invoice in one list, with the input tax
  // credit position for the financial year. Deliberately NOT shown to the
  // Procurement Managers — they enter invoices, they do not own the tax
  // position, and a GST return is not a purchasing document.
  { to: '/vendor-invoices', label: 'Vendor Invoices & ITC', icon: ReceiptIndianRupee, group: 'Procurement' },
  // Asked for by Accounts: sites buy locally in an emergency and those bills
  // exist nowhere else in the company — they are attachments on a goods
  // receipt. This is how Accounts finds them, site by site.
  // Asked for by the board. Read-only for everybody on it.
  { to: '/liabilities', label: 'Liabilities', icon: Landmark, group: 'Procurement' },
  // Board mandate 23-09-2026: the Accounts ledger's vendor balances as at the
  // cut-off, and the payments made against them since. Separate from the
  // goods-receipt payables above; changes no figure on any of them.
  //
  // 04-11-2026, migration 0228: taken off the menu. The opening balances now
  // live in Project Finance (frozen sheet, COB 31-10-2026), and two places to
  // load them would invite loading them twice. The screen, its route and its
  // tables are untouched - it simply has no menu entry.
  // { to: '/opening-liabilities', label: 'Vendor Opening Liabilities', icon: Landmark, group: 'Procurement' },
  // Read-only. Cash spent at site, recorded on the goods receipt, reported here.
  { to: '/petty-cash', label: 'Petty Cash Purchases', icon: Banknote, group: 'Procurement' },
  { to: '/grn-register', label: 'Goods Receipts', icon: PackageSearch, group: 'Procurement' },
  { to: '/credit-notes', label: 'Credit Notes', icon: ReceiptText, group: 'Procurement' },
  // Head of Stores sees this for the same reason he sees Vendor Payables and
  // Credit Notes: he raises the goods receipts these payments settle, and is
  // the one who gets asked whether a vendor was ever paid.
  { to: '/payments', label: 'Payments Made', icon: CreditCard, group: 'Procurement' },
  { to: '/rates', label: 'Last Purchase Rates', icon: IndianRupee, group: 'Procurement' },

  { to: '/grns', label: 'Goods Receipt (GRN)', icon: PackagePlus, group: 'Stores' },
  { to: '/mins', label: 'Material Issue (MIN)', icon: PackageMinus, group: 'Stores' },
  { to: '/mrns', label: 'Material Return (MRN)', icon: PackageCheck, group: 'Stores' },
  { to: '/inventory', label: 'Inventory', icon: Boxes, group: 'Stores' },

  { to: '/assets', label: 'Fixed Assets', icon: Truck, group: 'Registers' },
  { to: '/reports', label: 'Reports & Exports', icon: ScrollText, group: 'Registers' },
  { to: '/late-entries', label: 'Late Entries', icon: CalendarClock, group: 'Registers' },

  // Accounts and the board see the Vendors tab only - the screen itself decides
  // which tabs to draw, and refuses outright if there are none. The link is
  // shown to anyone who has at least one tab to look at.
  { to: '/masters', label: 'Master Data', icon: Building2, group: 'Administration' },
  // Closing a month is what stops a late document changing a figure Accounts
  // has already posted to Tally. Accounts closes; the administrator reopens.
  { to: '/periods', label: 'Accounting Periods', icon: CalendarCheck, group: 'Administration' },
  { to: '/users', label: 'Users & Roles', icon: Users, group: 'Administration' },
  // 29-09-2026, migration 0184: the business rules - tolerances, back-dating,
  // overdue days - in one place, for the System Administrator alone.
  { to: '/system-settings', label: 'System Settings', icon: SlidersHorizontal, group: 'Administration' },
  { to: '/sync', label: 'Sync Queue', icon: RefreshCw, group: 'Administration' },
]

/**
 * Roles created by the Board resolution of 11-08-2026 that must NOT see the
 * whole menu.
 *
 * Most NAV entries say `show: () => true`, which was right when every role was
 * either head office or site. Accounts is neither — the Board asked for "bare
 * minimal access, all CRUD operations related to payments only" — and a
 * Project Manager has no business on a goods receipt screen. Rather than
 * rewrite thirty predicates, these roles get an explicit allow-list applied on
 * top. Anything not named here is invisible to them.
 */



/**
 * The phone's bottom bar, role by role (13-10-2026). Each role gets the
 * screens it uses most; pickMobileTabs keeps only those canSeeRoute allows and
 * tops up from MOBILE_FALLBACK, so a role is never shown a screen it cannot
 * open. See components/layout/MobileTabBar.tsx.
 */
const MOBILE_CATALOGUE: Record<string, MobileTab> = {
  '/': { to: '/', label: 'Home', icon: Gauge, end: true },
  '/indents': { to: '/indents', label: 'Indent', icon: HardHat },
  '/prs': { to: '/prs', label: 'PR', icon: ClipboardList },
  '/pos': { to: '/pos', label: 'PO', icon: FileText },
  '/grns': { to: '/grns', label: 'GRN', icon: PackagePlus },
  '/mins': { to: '/mins', label: 'Issue', icon: PackageMinus },
  '/inventory': { to: '/inventory', label: 'Stock', icon: Boxes },
  '/liabilities': { to: '/liabilities', label: 'Liabilities', icon: Landmark },
  '/payables': { to: '/payables', label: 'Payables', icon: Wallet },
  '/rates': { to: '/rates', label: 'Rates', icon: IndianRupee },
  '/vendor-invoices': { to: '/vendor-invoices', label: 'Invoices', icon: ReceiptIndianRupee },
  '/payments': { to: '/payments', label: 'Payments', icon: CreditCard },
  '/petty-cash': { to: '/petty-cash', label: 'Petty cash', icon: Banknote },
  '/sync': { to: '/sync', label: 'Sync', icon: RefreshCw },
}

const STORE_TABS = ['/', '/indents', '/grns', '/mins', '/inventory']

const MOBILE_TABS_BY_ROLE: Partial<Record<UserRole, string[]>> = {
  cmd: ['/', '/pos', '/grns', '/inventory', '/liabilities'],
  cxo: ['/', '/pos', '/grns', '/inventory', '/liabilities'],
  project_head: ['/', '/prs', '/pos', '/inventory', '/liabilities'],
  project_manager: ['/', '/indents', '/prs', '/pos', '/inventory'],
  procurement_head: ['/', '/prs', '/pos', '/payables', '/liabilities'],
  procurement_manager: ['/', '/prs', '/pos', '/payables', '/rates'],
  accounts_spoc: ['/', '/vendor-invoices', '/payments', '/liabilities', '/petty-cash'],
  stores_head: STORE_TABS,
  store_executive: STORE_TABS,
  storeman: STORE_TABS,
  site_engineer: STORE_TABS,
  site_construction_manager: ['/', '/indents', '/prs', '/mins', '/inventory'],
  admin: ['/', '/pos', '/grns', '/inventory', '/sync'],
}

/** What any role not listed above had before: Home, PRs, GRN, Stock. */
const MOBILE_FALLBACK = ['/', '/prs', '/grns', '/inventory', '/pos', '/indents']

export function AppShell() {
  const { profile, site, signOut, priming } = useAuth()
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => { setOpen(false) }, [location.pathname])

  if (!profile) return null
  const role = profile.role
  // A role with no Materials side at all - today only the CFO, who opens
  // Project Finance alone (01-10-2026) - is sent to where it belongs rather
  // than shown a Materials shell. Every role that had Materials still has it,
  // so for them nothing here changes.
  if (!mayUseModule(role, 'materials')) return <Navigate to={landingFor(role)} replace />
  const entries = NAV.filter((n) => canSeeRoute(role, n.to))
  const groups = [...new Set(entries.map((e) => e.group))]

  return (
    <div className="min-h-screen lg:flex">
      {/* ---------- Sidebar ---------- */}
      <aside className={clsx(
        // Graphite sidebar - branding 10-10-2026 (Sample UI screens OVAL IMS).
        'no-print fixed inset-y-0 left-0 z-40 w-72 transform overflow-y-auto border-r border-sidebar-line bg-sidebar transition-transform lg:static lg:translate-x-0',
        open ? 'translate-x-0' : '-translate-x-full',
      )}>
        <div className="flex items-center gap-2 border-b border-sidebar-line px-4 py-3">
          {/* Branding 10-10-2026: the OVAL mark shown in WHITE on the graphite
              sidebar, as in the Board's sample screens. Same file and shape - a
              CSS filter only; the blue original is untouched everywhere else. */}
          <span className="flex h-10 w-10 shrink-0 items-center justify-center">
            <img src="/logo-oval.png" alt="OVAL" className="h-9 w-9 object-contain brightness-0 invert" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-white">OVAL IMS</p>
            <p className="truncate text-[11px] text-sidebar-muted">Procurement &amp; Stores</p>
          </div>
          <button className="rounded p-1 text-sidebar-muted hover:bg-sidebar-hover hover:text-white lg:hidden" onClick={() => setOpen(false)}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b border-sidebar-line bg-sidebar-hover/60 px-4 py-3">
          <p className="truncate text-sm font-semibold text-white">{profile.full_name}</p>
          <p className="truncate text-xs text-sidebar-text">{ROLE_LABEL[role]}</p>
          <p className="mt-1 truncate text-[11px] text-sidebar-muted">
            {site ? `${site.site_name} (${site.site_code})` : profile.site_id ?? '—'}
          </p>
          {isReadOnly(role) && (
            <span className="pill-grey mt-2"><ShieldCheck className="h-3 w-3" /> View only</span>
          )}
        </div>

        <nav className="space-y-4 px-3 py-4">
          {groups.map((g) => (
            <div key={g}>
              <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-sidebar-muted">{g}</p>
              <div className="space-y-0.5">
                {entries.filter((e) => e.group === g).map((e) => (
                  <NavLink
                    key={e.to}
                    to={e.to}
                    end={e.to === '/'}
                    className={({ isActive }) => clsx('nav-item', isActive && 'nav-item-active')}
                  >
                    <e.icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{e.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Sign out used to live here, at the foot of the sidebar. It moved to
            the header on 09-09-2026: on a phone the sidebar is a drawer that
            has to be opened first, so the one control everybody eventually
            needs was the one hardest to reach. */}
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-slate-900/40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* ---------- Main ---------- */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print sticky top-0 z-20 flex items-center gap-1.5 border-b border-slate-200 bg-white/95 px-2 py-2.5 sm:gap-3 sm:px-4 backdrop-blur">
          <button className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden" onClick={() => setOpen(true)}>
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-800">
              {entries.find((e) => e.to === location.pathname)?.label ?? 'OVAL IMS'}
            </p>
          </div>
          {/* Left of Sign out, so the destructive control stays the rightmost
              thing before the bell and nobody reaches for it by muscle memory
              while meaning to change sides. */}
          <ModuleSwitch current="materials" />
          {/* 14-10-2026: change my password, just left of Sign out. */}
          <ChangePasswordButton />
          {/* Immediately to the LEFT of the bell, and on every screen size. */}
          <button
            onClick={async () => { await signOut(); navigate('/login') }}
            title={`Sign out of ${profile.full_name}'s account`}
            className="flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <LogOut className="h-4 w-4" />
            {/* The word is dropped on a narrow phone so it never crowds the
                page title, but the icon and its tooltip stay. */}
            <span className="hidden sm:inline">Sign out</span>
          </button>
          <NotificationBell module="materials" />
          {priming && (
            <span className="hidden items-center gap-1.5 text-xs text-slate-500 sm:flex">
              <Spinner /> downloading master data
            </span>
          )}
          <SyncIndicator />
        </header>

        <OfflineBanner />

        <main className="min-w-0 flex-1 px-4 py-4 sm:px-6 sm:py-6">
          <Outlet />
        </main>

        {/* Which build is on the server, in one line. It is here because on
            15-09-2026 a Phase 5 database was tested against a Phase 4b front
            end and nothing on screen said so. See src/lib/build.ts. */}
        <p className="no-print px-4 pb-2 text-center text-[10px] text-slate-300 sm:px-6">
          {BUILD_LABEL}
        </p>

        <MobileTabSpacer />

        {/* ---------- Mobile tab bar ---------- (role-wise since 13-10-2026) */}
        <MobileTabBar tabs={pickMobileTabs(
          MOBILE_TABS_BY_ROLE[role] ?? [], MOBILE_FALLBACK, MOBILE_CATALOGUE,
          (to) => canSeeRoute(role, to),
        )} />
      </div>
    </div>
  )
}

function SyncIndicator() {
  const { online, syncing, pending, failed } = useSyncStore()
  const navigate = useNavigate()

  let tone = 'text-emerald-600', Icon = CheckCheck, label = 'Synced'
  if (!online) { tone = 'text-slate-500'; Icon = CloudOff; label = 'Offline' }
  else if (syncing) { tone = 'text-sky-600'; Icon = RefreshCw; label = 'Syncing' }
  else if (failed > 0) { tone = 'text-red-600'; Icon = AlertCircle; label = `${failed} failed` }
  else if (pending > 0) { tone = 'text-amber-600'; Icon = Package; label = `${pending} queued` }

  return (
    <button
      onClick={() => (pending || failed ? navigate('/sync') : flushOutbox())}
      className={clsx('flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold hover:bg-slate-100', tone)}
      title={online ? 'Tap to sync now' : 'Working offline — your entries are saved on this device'}
    >
      <Icon className={clsx('h-4 w-4', syncing && 'animate-spin')} />
      <span className="hidden sm:inline">{label}</span>
      {(pending > 0 || failed > 0) && <span className="sm:hidden">{pending + failed}</span>}
    </button>
  )
}

function OfflineBanner() {
  const { online, pending, failed } = useSyncStore()
  if (online && !failed) return null
  return (
    <div className={clsx(
      'no-print px-4 py-2 text-xs font-medium sm:px-6',
      failed ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-900',
    )}>
      {!online && (
        <span className="flex items-center gap-2">
          <CloudOff className="h-4 w-4" />
          Working offline. Everything you enter is saved on this device
          {pending > 0 && <strong>({pending} waiting to sync)</strong>}
          and will upload by itself when the connection returns.
        </span>
      )}
      {online && failed > 0 && (
        <span className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          {failed} queued {failed === 1 ? 'entry' : 'entries'} could not be uploaded.
          <NavLink to="/sync" className="underline">Open the sync queue</NavLink>
        </span>
      )}
    </div>
  )
}
